import { Box, useTheme } from "@mui/material";
import { PieChart, useDrawingArea, useItemTooltip } from "@mui/x-charts";

import Paper from "@mui/material/Paper";
import PropTypes from "prop-types";
import SyncIcon from "@mui/icons-material/Sync";
import { styled } from "@mui/material/styles";

const TooltipPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(1),
    pointerEvents: "none",
    whiteSpace: "nowrap",
}));

const TooltipRow = styled("div")({
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "2px 0",
});

const ColorIndicator = styled("div")(({ color }) => ({
    width: 10,
    height: 10,
    borderRadius: "50%",
    backgroundColor: color,
    flexShrink: 0,
}));

const AllItemsTooltip = ({ data }) => {
    const tooltipData = useItemTooltip();
    const { width } = useDrawingArea();

    if (!tooltipData) {
        return null;
    }

    return (
        <TooltipPaper
            elevation={3}
            sx={{
                position: "absolute",
                left: width + 10,
                top: "50%",
                transform: "translateY(-50%)",
            }}
        >
            {data.map((item, index) => (
                <TooltipRow key={index}>
                    <ColorIndicator color={item.color} />
                    <span style={{ fontSize: 12 }}>
                        {item.label}: {item.value}
                    </span>
                </TooltipRow>
            ))}
        </TooltipPaper>
    );
};

export const PipelineInstanceStatusPieChart = ({
    pipelineInstancesCountPerStatus,
}) => {
    const theme = useTheme();

    const hasOngoing =
        pipelineInstancesCountPerStatus.find((item) => item.label === "Ongoing")
            ?.value > 0;

    return (
        <Box
            sx={{
                position: "relative",
                width: 60,
                height: 60,
            }}
        >
            {hasOngoing && (
                <SyncIcon
                    sx={{
                        color: theme.palette.info.main,
                        position: "absolute",
                        top: "50%",
                        left: 29,
                        opacity: 0.3,
                        fontSize: 70,
                        zIndex: 0,
                        animation: "traitGroupSpin 8s linear infinite",
                        transformOrigin: "center",
                        "@keyframes traitGroupSpin": {
                            "0%": {
                                transform:
                                    "translate(-50%, -50%) scaleX(-1) rotate(0deg)",
                            },
                            "100%": {
                                transform:
                                    "translate(-50%, -50%) scaleX(-1) rotate(-360deg)",
                            },
                        },
                    }}
                />
            )}
            <PieChart
                series={[
                    {
                        data: pipelineInstancesCountPerStatus,
                        outerRadius: 14,
                        cx: 25,
                    },
                ]}
                width={60}
                height={60}
                slotProps={{
                    legend: { hidden: true },
                }}
                slots={{
                    itemContent: () => (
                        <AllItemsTooltip
                            data={pipelineInstancesCountPerStatus}
                        />
                    ),
                }}
                sx={{
                    position: "relative",
                    zIndex: 1,
                }}
            />
        </Box>
    );
};

PipelineInstanceStatusPieChart.propTypes = {
    pipelineInstancesCountPerStatus: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string.isRequired,
            value: PropTypes.number.isRequired,
            color: PropTypes.string.isRequired,
        })
    ).isRequired,
};
